"use client";

import { usePostHog } from "@posthog/next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FEATURE_ANNOUNCEMENT_DELAY_MS } from "@/constants/featureAnnouncements";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useAnyFloatingSurfaceVisible } from "@/hooks/useFloatingSurface";
import { useUserAcknowledgement } from "@/hooks/useUserAcknowledgement";
import type { FeatureAnnouncement } from "@/types/featureAnnouncement";
import type { UserAcknowledgementStatus } from "@/utils/acknowledgements/userAcknowledgements";
import {
  buildFeatureAnnouncementAcknowledgement,
  isFeatureAnnouncementDismissedThisSession,
  markFeatureAnnouncementDismissedThisSession,
} from "@/utils/featureAnnouncements/featureAnnouncements";

const NO_ANNOUNCEMENT = { id: "none", version: "v0" } as const;

interface UseFeatureAnnouncementOptions {
  /** Hides the card without dismissing it, e.g. while another UI covers the screen. */
  suppressed?: boolean;
  delayMs?: number;
}

interface UseFeatureAnnouncementResult {
  isVisible: boolean;
  dismiss: () => void;
  accept: () => void;
}

export function useFeatureAnnouncement(
  announcement: FeatureAnnouncement | null,
  { suppressed = false, delayMs = FEATURE_ANNOUNCEMENT_DELAY_MS }: UseFeatureAnnouncementOptions = {}
): UseFeatureAnnouncementResult {
  const { user } = useAuthContext();
  const posthog = usePostHog();
  const otherSurfaceVisible = useAnyFloatingSurfaceVisible();

  const orgId = user?.orgId;
  const descriptor = useMemo(
    () => buildFeatureAnnouncementAcknowledgement(announcement ?? NO_ANNOUNCEMENT, orgId),
    [announcement, orgId]
  );
  const { isAcknowledged, isResolved, acknowledge } = useUserAcknowledgement({
    descriptor,
    orgId: announcement ? orgId : null,
    userId: announcement ? user?.id : null,
  });

  const [closedAnnouncement, setClosedAnnouncement] = useState<FeatureAnnouncement | null>(null);
  const [delayedAnnouncement, setDelayedAnnouncement] = useState<FeatureAnnouncement | null>(null);
  const shownRef = useRef<FeatureAnnouncement | null>(null);
  const isSessionDismissed = useMemo(
    () => announcement !== null && isFeatureAnnouncementDismissedThisSession(announcement, orgId),
    [announcement, orgId]
  );

  useEffect(() => {
    if (!announcement) return;
    const timer = setTimeout(() => setDelayedAnnouncement(announcement), delayMs);
    return () => {
      clearTimeout(timer);
      // Returning to the same announcement must wait again, so the
      // acknowledgement lookup for it resolves before the card can paint.
      setDelayedAnnouncement(null);
    };
  }, [announcement, delayMs]);

  const isEligible =
    announcement !== null &&
    Boolean(user?.id) &&
    isResolved &&
    !isAcknowledged &&
    delayedAnnouncement === announcement &&
    closedAnnouncement !== announcement &&
    !isSessionDismissed;
  const isVisible = isEligible && !otherSurfaceVisible && !suppressed;

  useEffect(() => {
    if (!isVisible || !announcement || shownRef.current === announcement) return;
    shownRef.current = announcement;
    posthog?.capture("feature_announcement_shown", {
      announcement_id: announcement.id,
      surface: announcement.surface,
    });
  }, [announcement, isVisible, posthog]);

  const close = useCallback(
    (status: UserAcknowledgementStatus, event: string) => {
      if (!announcement) return;
      markFeatureAnnouncementDismissedThisSession(announcement, orgId);
      setClosedAnnouncement(announcement);
      posthog?.capture(event, {
        announcement_id: announcement.id,
        surface: announcement.surface,
      });
      void acknowledge(status, { surface: announcement.surface });
    },
    [acknowledge, announcement, orgId, posthog]
  );

  const dismiss = useCallback(
    () => close("dismissed", "feature_announcement_dismissed"),
    [close]
  );
  const accept = useCallback(
    () => close("seen", "feature_announcement_primary_clicked"),
    [close]
  );

  return { isVisible, dismiss, accept };
}
